import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { HiXMark } from 'react-icons/hi2';
import { getAllStockShortagesByLocalIdAndDateRange,
  getAllMermasByIngredientId,
  toggleStockFavorite,
  toggleStockFollowing,
} from '../../../../../../../../crud/management_api/stock_management';
import { getAllProductMovementsByIdAndDateRange } from '../../../../../../../../crud/management_api/storage_unit_movements';
import LoadingSpinner from '../../../../../../../../components/common/LoadingSpinner';
import { toast } from 'react-toastify';
import { ProductStock } from '../../../../types';
import Summary from './components/Summary/Summary';
import Movements from './components/Movements/Movements';
import Shortages from './components/Shortages/Shortages';
import Mermas from './components/Mermas/Mermas';
import './StockDetails.scss';

interface StockDetailsProps {
  show: boolean;
  onClose: () => void;
  stockItem: ProductStock | null;
}

interface ExtendedProductStock extends ProductStock {
  is_favorite?: boolean;
  is_following?: boolean;
  sensitivity?: number;
}

interface Movement {
  movement_id: number;
  is_purchase: boolean;
  is_consumption: boolean;
  is_manual: boolean;
  is_system: boolean;
  storage_unit_id_from: number | null;
  storage_unit_id_to: number | null;
  storage_unit_id_from_name: string | null;
  storage_unit_id_to_name: string | null;
  product_id: string | null;
  new_quantity: number;
  old_quantity: number;
  unidad_medida_id: number;
  local_timestamp: string;
  timestamp: string;
  user_id: number;
  username: string;
  local_id: number;
  acronym: string;
}

interface Shortage {
  shortage_id: number;
  storage_unit_id: number;
  storage_unit_name: string;
  item_id: number;
  item_name: string;
  ingredient_id: number;
  ingredient_name: string;
  utility_id: number;
  quantity: number;
  solved: boolean;
  unidad_medida_id: number;
  local_timestamp: string;
  timestamp: string;
  local_id: number;
  local_session_id: number;
  unidad_medida_name: string;
}

interface Merma {
  merma_id: number;
  ingredient_id: number;
  storage_unit_id: number;
  quantity: number;
  reason: string;
  local_timestamp: string;
  timestamp: string;
  user_id: number;
  username: string;
}

const StockDetails: React.FC<StockDetailsProps> = ({ show, onClose, stockItem }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [isLoading, setIsLoading] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);

  const [productStock, setProductStock] = useState<ExtendedProductStock | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [shortages, setShortages] = useState<Shortage[]>([]);
  const [mermas, setMermas] = useState<Merma[]>([]);

  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  });

  useEffect(() => {
    if (!show || !stockItem) return;

    const stockItemAny = stockItem as any;
    const extendedStockItem: ExtendedProductStock = {
      ...stockItem,
      is_favorite: stockItemAny.is_favorite || false,
      is_following: stockItemAny.is_following || false,
      sensitivity: stockItemAny.sensitivity || 0
    };

    setProductStock(extendedStockItem);
    setIsFavorite(extendedStockItem.is_favorite || false);
    setIsMonitoring(extendedStockItem.is_following || false);

    const fetchMovementsData = async () => {
      setIsLoading(true);
      try {
        const ingredientId = parseInt(stockItem.product_id.split('-')[1]);
        const storageUnitId = stockItem.storage_unit_id;
        const user = JSON.parse(localStorage.getItem('user') ?? '{}');
        const localId = user?.local_id;

        if (!localId) {
          console.warn('Local ID not found in localStorage');
          toast.error('No se pudo obtener el ID del local');
          setIsLoading(false);
          return;
        }

        const [movementsResponse, shortagesResponse, mermasResponse] = await Promise.all([
          getAllProductMovementsByIdAndDateRange(stockItem.product_id, startDate, endDate),
          getAllStockShortagesByLocalIdAndDateRange(
            localId, startDate, endDate, ingredientId, storageUnitId
          ),
          getAllMermasByIngredientId(ingredientId, startDate, endDate)
        ]);

        setMovements(movementsResponse.data || []);
        setShortages(shortagesResponse.data || []);
        setMermas(mermasResponse.data || []);
      } catch (error) {
        console.error('Error fetching movements data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMovementsData();
  }, [show, stockItem, startDate, endDate]);

  const handleClose = () => {
    setActiveTab('summary');
    setProductStock(null);
    setMovements([]);
    setShortages([]);
    setMermas([]);
    onClose();
  };

  const toggleFavorite = async () => {
    if (!stockItem) return;
    const currentIsFavorite = isFavorite;
    setIsFavorite(!currentIsFavorite);
    toast.success(!currentIsFavorite ? 'Agregado a favoritos' : 'Removido de favoritos');
    try {
      const ingredientId = parseInt(stockItem.product_id.split('-')[1]);
      await toggleStockFavorite(ingredientId, stockItem.storage_unit_id);
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setIsFavorite(currentIsFavorite);
      toast.error('Error al actualizar favoritos. Se revirtió el cambio.');
    }
  };

  const toggleMonitoring = async () => {
    if (!stockItem) return;
    const currentIsMonitoring = isMonitoring;
    setIsMonitoring(!currentIsMonitoring);
    toast.success(!currentIsMonitoring ? 'Monitoreo activado' : 'Monitoreo desactivado');
    try {
      const ingredientId = parseInt(stockItem.product_id.split('-')[1]);
      await toggleStockFollowing(ingredientId, stockItem.storage_unit_id);
    } catch (error) {
      console.error('Error toggling monitoring:', error);
      setIsMonitoring(currentIsMonitoring);
      toast.error('Error al actualizar monitoreo. Se revirtió el cambio.');
    }
  };

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="loading-container">
          <LoadingSpinner text="Cargando detalles..." transparent={true} />
        </div>
      );
    }

    switch (activeTab) {
      case 'summary':
        return (
          <Summary
            productStock={productStock}
            movements={movements}
            shortages={shortages}
            mermas={mermas}
            isFavorite={isFavorite}
            isMonitoring={isMonitoring}
            onToggleFavorite={toggleFavorite}
            onToggleMonitoring={toggleMonitoring}
          />
        );
      case 'movements':
        return (
          <Movements
            movements={movements}
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />
        );
      case 'shortages':
        return <Shortages shortages={shortages} />;
      case 'mermas':
        return <Mermas mermas={mermas} />;
      default:
        return null;
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      dialogClassName="mp-modal"
      className="stock-details-modal"
      fullscreen="lg-down"
    >
      <Modal.Header className="mp-modal-header stock-details-header" closeButton={false}>
        <div className="header-content">
          <Modal.Title className="stock-details-title">
            {stockItem?.name || 'Detalles de Stock'}
          </Modal.Title>

          <div className="mp-tab-group">
            {[
              { key: 'summary', label: 'Resumen' },
              { key: 'movements', label: 'Movimientos' },
              { key: 'shortages', label: 'Quiebres' },
              { key: 'mermas', label: 'Mermas' },
            ].map(tab => (
              <button
                key={tab.key}
                className={`mp-tab ${activeTab === tab.key ? 'mp-tab-active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <button className="mp-modal-close" onClick={handleClose}>
          <HiXMark size={20} />
        </button>
      </Modal.Header>

      <Modal.Body className="stock-details-body">
        {renderTabContent()}
      </Modal.Body>
    </Modal>
  );
};

export default StockDetails;
